/**
 * Org Settings → Single sign-on (#1792, SSO Slice 1).
 *
 * Where a customer connects their own company sign-in. Owner and admin
 * only, and the tab is hidden entirely unless the org's plan includes
 * the feature — the same treatment Run budgets gets, and for the same
 * reason: an unfinished-looking locked screen sells nothing.
 *
 * **Three states, in order.** Nothing connected (the form), connected
 * but the domain is unproven (the DNS record and a check button), and
 * working. They are steps rather than tabs because they only ever
 * happen in this order, and a customer part-way through needs to see
 * what is left rather than hunt for it.
 *
 * **Why the customer does this and not us.** The sign-on plugin refuses
 * to register a connection for anyone who is not an owner or admin of
 * the organization, so an OttoTester superadmin cannot do it on their behalf.
 * It also means the customer's client secret never has to be sent to
 * us, which matters to the buyers driving this work.
 *
 * **Why the connection calls go straight to the plugin's client.**
 * `authClient.sso.*` is the one way a connection is created, so there
 * is no second path to keep in step. The plan check that our own screen
 * would otherwise enforce lives on the server in front of those same
 * endpoints, because they are reachable without this screen.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Button, Card, CardContent, CardHeader, CardTitle, CardDescription, Checkbox, Input, Label, Badge } from '@/components/ui'
import { authClient } from '@/auth/authClient'
import { orgsAPI, type OrgWithRole, type OrgSsoSettings } from '@/lib/api'
import { toUserMessage } from '@/lib/userMessage'
import { useCanI } from '@/hooks/useCanI'

/** One connected company sign-in, as this screen needs it. The plugin
 * returns more; nothing else is shown, and the secret is never returned
 * by the plugin at all. */
interface Connection {
  providerId: string
  issuer: string
  domain: string
  domainVerified?: boolean
  domainVerificationToken?: string
}

/** A stable id per org so a second connection cannot collide with a
 * first. The plugin rejects a duplicate, and a customer who removes and
 * re-adds should land on the same identifier rather than accumulating
 * numbered ones. */
function providerIdForOrg(orgId: string): string {
  return `org-${orgId}`
}

export function OrgSingleSignOn({ org }: { org: OrgWithRole }) {
  const canEdit = useCanI('org.writeSettings', org._id)

  const [loading, setLoading] = useState(true)
  const [connection, setConnection] = useState<Connection | null>(null)
  const [settings, setSettings] = useState<OrgSsoSettings | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Form fields, only used before a connection exists.
  const [domain, setDomain] = useState('')
  const [issuer, setIssuer] = useState('')
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')

  const load = useCallback(async () => {
    setError(null)
    try {
      const [providers, ssoSettings] = await Promise.all([
        authClient.sso.providers(),
        orgsAPI.getSsoSettings(org._id),
      ])
      const rows = ((providers as { data?: unknown }).data ?? []) as Connection[]
      setConnection(rows.find((r) => r.providerId === providerIdForOrg(org._id)) ?? null)
      setSettings(ssoSettings)
    } catch (err) {
      setError(toUserMessage(err, "We couldn't load your single sign-on settings."))
    } finally {
      setLoading(false)
    }
  }, [org._id])

  useEffect(() => {
    void load()
  }, [load])

  async function handleConnect(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setNotice(null)
    setBusy(true)
    try {
      const result = await authClient.sso.register({
        providerId: providerIdForOrg(org._id),
        issuer: issuer.trim(),
        domain: domain.trim().toLowerCase(),
        organizationId: org._id,
        oidcConfig: { clientId: clientId.trim(), clientSecret: clientSecret.trim() },
      })
      if ((result as { error?: { message?: string } }).error) {
        throw new Error((result as { error?: { message?: string } }).error?.message)
      }
      setClientSecret('')
      await load()
    } catch (err) {
      setError(toUserMessage(err, "We couldn't save your company sign-in. Check the details and try again."))
    } finally {
      setBusy(false)
    }
  }

  async function handleCheckDomain() {
    if (!connection) return
    setError(null)
    setNotice(null)
    setBusy(true)
    try {
      const result = await authClient.sso.verifyDomain({ providerId: connection.providerId })
      if ((result as { error?: { message?: string } }).error) {
        throw new Error((result as { error?: { message?: string } }).error?.message)
      }
      await load()
      setNotice('Your domain is confirmed. Your people can sign in with your company account now.')
    } catch {
      // A failed check is the ordinary case right after adding a record,
      // not an error worth a red banner. DNS takes minutes to spread.
      setNotice("We couldn't find the record yet. DNS can take a few minutes. Try again shortly.")
    } finally {
      setBusy(false)
    }
  }

  async function handleRemove() {
    if (!connection) return
    setError(null)
    setNotice(null)
    setBusy(true)
    try {
      await authClient.sso.deleteProvider({ providerId: connection.providerId })
      await load()
    } catch (err) {
      setError(toUserMessage(err, "We couldn't remove your company sign-in. Try again."))
    } finally {
      setBusy(false)
    }
  }

  async function handleWorkspacesChange(workspaceId: string, checked: boolean) {
    if (!settings) return
    const next = checked
      ? [...settings.defaultWorkspaceIds, workspaceId]
      : settings.defaultWorkspaceIds.filter((id) => id !== workspaceId)
    setSettings({ ...settings, defaultWorkspaceIds: next })
    try {
      await orgsAPI.updateSsoSettings(org._id, next)
    } catch (err) {
      setError(toUserMessage(err, "We couldn't save that change."))
      await load()
    }
  }

  if (loading) return null

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h2 className="text-lg font-semibold">Single sign-on</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Let your people sign in with your company account instead of a password.
        </p>
      </div>

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      {notice && <p className="text-sm text-muted-foreground">{notice}</p>}

      {!connection && (
        <Card>
          <CardHeader>
            <CardTitle>Your company sign-in</CardTitle>
            <CardDescription>
              Copy these from the app registration your IT team created for OttoTester.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleConnect} className="space-y-4" aria-label="Connect company sign-in">
              <div className="space-y-2">
                <Label htmlFor="sso-domain">Company email domain</Label>
                <Input
                  id="sso-domain"
                  required
                  placeholder="acme.com"
                  value={domain}
                  disabled={!canEdit}
                  onChange={(e) => setDomain(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  People with an email at this domain will be offered your company sign-in.
                  You&apos;ll prove you own it in the next step.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="sso-issuer">Sign-in web address</Label>
                <Input
                  id="sso-issuer"
                  required
                  placeholder="https://login.microsoftonline.com/your-tenant-id/v2.0"
                  value={issuer}
                  disabled={!canEdit}
                  onChange={(e) => setIssuer(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Microsoft calls this the authority or issuer address.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="sso-client-id">Application ID</Label>
                <Input
                  id="sso-client-id"
                  required
                  value={clientId}
                  disabled={!canEdit}
                  onChange={(e) => setClientId(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sso-secret">Secret value</Label>
                <Input
                  id="sso-secret"
                  type="password"
                  required
                  value={clientSecret}
                  disabled={!canEdit}
                  onChange={(e) => setClientSecret(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Stored encrypted. We show it once and never again.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <Button type="submit" disabled={!canEdit || busy}>
                  {busy ? 'Saving…' : 'Save'}
                </Button>
                <span className="text-xs text-muted-foreground">
                  Nothing changes for your people until the next step is done.
                </span>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {connection && !connection.domainVerified && (
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle>Prove you own {connection.domain}</CardTitle>
                <CardDescription>
                  Add this record to your DNS, then choose Check now. This stops anyone else
                  from claiming your domain.
                </CardDescription>
              </div>
              <Badge variant="secondary">Not confirmed yet</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <dl className="grid grid-cols-[5rem_1fr] gap-x-4 gap-y-2 text-sm rounded-md border p-3 bg-muted/40">
              <dt className="text-muted-foreground">Type</dt>
              <dd className="font-mono">TXT</dd>
              <dt className="text-muted-foreground">Name</dt>
              <dd className="font-mono break-all">_ottotester-sso.{connection.domain}</dd>
              <dt className="text-muted-foreground">Value</dt>
              <dd className="font-mono break-all">
                {connection.domainVerificationToken ?? 'Reload to see the value'}
              </dd>
            </dl>
            <div className="flex gap-2">
              <Button onClick={handleCheckDomain} disabled={!canEdit || busy}>
                {busy ? 'Checking…' : 'Check now'}
              </Button>
              <Button variant="outline" onClick={handleRemove} disabled={!canEdit || busy}>
                Remove
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {connection?.domainVerified && (
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle>Connected · {connection.domain}</CardTitle>
                <CardDescription>
                  Your people can now sign in with their {org.name} account.
                </CardDescription>
              </div>
              <Badge variant="success">Working</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={handleRemove} disabled={!canEdit || busy}>
              Remove
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>What new people can see</CardTitle>
          <CardDescription>
            Someone signing in for the first time joins {org.name} as a reviewer, which is
            read-only. Pick which workspaces they can read.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {settings?.workspaces.length === 0 && (
            <p className="text-sm text-muted-foreground">
              This organization has no workspaces yet.
            </p>
          )}
          {settings?.workspaces.map((ws) => (
            <div key={ws._id} className="flex items-center gap-3">
              <Checkbox
                id={`sso-ws-${ws._id}`}
                checked={settings.defaultWorkspaceIds.includes(ws._id)}
                disabled={!canEdit}
                onCheckedChange={(v) => void handleWorkspacesChange(ws._id, v === true)}
              />
              <Label htmlFor={`sso-ws-${ws._id}`} className="font-normal cursor-pointer">
                {ws.name}
              </Label>
            </div>
          ))}
          <p className="text-xs text-muted-foreground">
            Leave these unchecked and new people sign in with no access until you give them
            some. Changing this only affects people signing in for the first time.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
